import { h, Component } from 'preact';
import Store from 'store/store';
import { joinClasses, sortObjectByOrder } from 'utils/utils';
import { navigateInternally } from 'utils/bolt';
import { drawCard, feedSize, storySize } from 'utils/poster';

import { TakeAgain } from 'components/bolt/icons/quizPhotoboothIcons';
import HTML from 'components/core/html/html';
import Socials from 'components/bolt/socials/socials';

import s from 'components/bolt/quizPhotobooth/shareable.sss';

export default class Shareable extends Component {
	constructor(props) {
		super(props);
		this.backStep = this.backStep.bind(this);
		this.onTouchStart = this.onTouchStart.bind(this);
		this.onTouchMove = this.onTouchMove.bind(this);
		this.onTouchEnd = this.onTouchEnd.bind(this);
		this.shareOrDownload = this.shareOrDownload.bind(this);
		this.resetShareable = this.resetShareable.bind(this);
		this.handleLinkClick = this.handleLinkClick.bind(this);
		this.restart = this.restart.bind(this);
		this.$videoFeed = null;
		this.$videoStories = null;
		this.imageRoot = this.props.appData.urls.roots.image;

		this.sortedResultData = sortObjectByOrder(this.props.resultData);

		this.frameCanvases = {};
		this.state = {
			canShare: false,
			currentCard: 0,
		};
	}

	get appData() {
		return this.props.appData || Store.get().appData;
	}
	get fontStyles() {
		return this.appData?.styling?.fontVariables || {};
	}
	get copy() {
		return this.appData?.copy?.baseCopy || {};
	}

	get cards() {
		const cards = ['story', 'feed'];
		if (!this.props.shareable?.storiesFirst) cards.reverse();
		return cards;
	}

	async componentDidMount() {
		this.redraw();
	}

	componentDidUpdate(prevProps) {
		if (this.props.cardData !== prevProps.cardData) {
			this.redraw();
		}
	}

	async drawCard(mode, ctx) {
		if (!ctx) return;
		drawCard(this.props.cardData, ctx, mode);
		const id = this[`renderId-${mode}`] = (this[`renderId-${mode}`] || 0) + 1;
		const blob = await new Promise(resolve => ctx.canvas.toBlob(resolve, 'image/jpeg', .8));
		if (id !== this[`renderId-${mode}`]) return;
		if (this.state[`image-${mode}`]) {
			URL.revokeObjectURL(this.state[`image-${mode}`]);
		}
		this.setState({ [`blob-${mode}`]: blob, [`image-${mode}`]: URL.createObjectURL(blob) });
	}

	redraw() {
		this.drawCard('feed', this.feedContext);
		this.drawCard('story', this.storyContext);
	}

	restart() {
		this.props.backToIntro();
	}

	onTouchStart(e) {
		if (e.touches.length === 1) {
			this.lastClientX = e.touches[0].clientX;
		}
	}

	onTouchMove(e) {
		if (e.touches.length === 1) {
			const clientX = e.touches[0].clientX;
			const diffX = clientX - this.lastClientX;
			this.diffX = diffX;
		}
	}

	onTouchEnd() {
		if (Math.abs(this.diffX) > 15) {
			const card = this.diffX > 0 ? 0 : 1;
			this.swapToCard(card);
		}
		this.diffX = null;
		this.lastClientX = null;
	}

	resetShareable() {
		this.setState({ currentCard: 0 });
	}

	swapToCard(card) {
		if (card === this.state.currentCard) return;
		this.setState({ currentCard: card });
		Store.emit('TRACK_QUIZPHOTOBOOTH_CAROUSEL_SWIPE');
	}

	handleLinkClick() {
		const path = this.props.shareable.link.path;
		if (path.startsWith('http') || path.startsWith('mailto:')) {
			let link = document.createElement('a');
			link.href = path;
			link.target = '_blank';
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else {
			navigateInternally(path);
		}
	}

	async shareOrDownload() {
		const { currentCard } = this.state;
		const { isDesktopDevice } = this.props;
		const type = this.cards[currentCard];

		const filename = `shareable-${type}.jpg`;
		const blob = this.state[`blob-${type}`];
		if (!blob) return;
		const files = [new File([blob], filename, { type: 'image/jpeg' })];
		const shareData = { files };
		const canShare = navigator.canShare?.(shareData);
		const useDownload = isDesktopDevice || !canShare;

		const trackingType = useDownload ? 'save' : 'share';
		let trackingFormatLabel = type === 'feed' ? 'feed' : 'stories';
		Store.emit('TRACK_QUIZPHOTOBOOTH_SHARE_CARD_CLICK', trackingType, trackingFormatLabel);

		if (useDownload) {
			const link = document.createElement('a');
			link.download = filename;
			link.href = this.state[`image-${type}`];
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else {
			navigator.share(shareData);
		}
	}

	backStep() {
		this.props.backStep();
		Store.emit('CUSTOM_EVENT', {
			category: 'carousel',
			action: 'carousel-back-click'
		});
	}

	renderBreadcrumbs() {
		return (
			<div class={s.breadcrumbs}>
				{[...Array(2)].map((_, card) => (
					<button
						class={joinClasses(s.crumb, this.state.currentCard === card && s.active)}
						onClick={() => this.swapToCard(card)}
						key={card}
					/>
				))}
			</div>
		);
	}

	canvasRef(type, e) {
		if (e) {
			this[`${type}Context`] = e.getContext('2d');
		}
	}

	renderCard(type, key) {
		const isActive = this.state.currentCard === key;
		const size = type === 'feed' ? feedSize : storySize;
		const img = this.state[`image-${type}`];

		return (
			<div class={joinClasses(s.transition, s[type], isActive ? s.active : s.inactive)} key={key}>
				<div class={s.cardContainer}>
					<div class={s.canvasWrapper} onClick={() => this.swapToCard(key)}>
						<canvas ref={e => this.canvasRef(type, e)} {...size} />
						{!!img && <img src={img} alt="" />}
					</div>
				</div>
			</div>
		);
	}

	renderSocials() {
		const socials = this.appData?.header?.powsterHeader?.follow;
		if (!socials) return;
		return (
			<div class={s.socials}>
				<HTML class={joinClasses(s.cta, this.fontStyles?.quizPhotoboothMessage)} content={this.copy.$QUIZPHOTOBOOTH_FOLLOW_US} markdown />
				<Socials socials={socials} iconStyle={s.socialIcon} key="socials" />
			</div>
		);
	}

	renderBackButton() {
		return (
			<button class={s.backContainer} onClick={this.backStep}>
				<HTML tag="span" content={this.copy.$QUIZPHOTOBOOTH_BACK} markdown inline />
			</button>
		);
	}

	renderFinalShareable() {
		const { assets, shareable, isDesktopDevice, isDesktopWidth, hasQuiz } = this.props;
		const fontStyles = this.fontStyles;
		const copy = this.copy;
		const { currentCard } = this.state;

		const restartButton = (
			<button class={joinClasses(s.takeAgain, !isDesktopWidth && s.mobile)} onClick={this.restart}>
				<TakeAgain />
				<HTML class={joinClasses(s.cta, s.takeAgainText)} content={hasQuiz ? copy.$QUIZPHOTOBOOTH_RETAKE_QUIZ : copy.$QUIZPHOTOBOOTH_RETAKE_PHOTOBOOTH} markdown inline />
			</button>
		);

		const cards = this.cards;
		const shareCopy = {
			story: `${copy.$QUIZPHOTOBOOTH_SHARE} ${copy.$QUIZPHOTOBOOTH_STORIES}`,
			feed: `${copy.$QUIZPHOTOBOOTH_SHARE} ${copy.$QUIZPHOTOBOOTH_FEED}`,
		};
		return (
			<div class={s.finalShareableContainer}>
				{!isDesktopWidth && restartButton}
				<div
					class={joinClasses(s.carousel, this.props.creativeSpace && s.creativeSpace)}
					onTouchStart={this.onTouchStart}
					onTouchMove={this.onTouchMove}
					onTouchEnd={this.onTouchEnd}
				>
					{cards.map((type, i) => this.renderCard(type, i))}

					<div class={s.nav} key="nav">
						<button class={s.prevBtn} onClick={() => this.swapToCard(currentCard - 1)} disabled={currentCard < 1} />
						<button class={s.nextBtn} onClick={() => this.swapToCard(currentCard + 1)} disabled={currentCard >= cards.length - 1} />
					</div>
				</div>
				{this.renderBreadcrumbs()}
				<button class={joinClasses(s.buttonPrimary, s.shareButton)} onClick={this.shareOrDownload}>
					{isDesktopDevice && <span class={joinClasses(s.buttonText, s.show)}>{copy.$QUIZPHOTOBOOTH_DOWNLOAD}</span>}
					{!isDesktopDevice && cards.map((type, i) => (
						<span class={joinClasses(currentCard === i && s.show)} key={type}>{shareCopy[type]}</span>
					))}
				</button>
				{isDesktopWidth && restartButton}
				<HTML class={joinClasses(s.cta, fontStyles?.quizPhotoboothMessage, s.hashtags)} content={copy.$QUIZPHOTOBOOTH_HASHTAG_CTA} markdown />
				{shareable.showSocialLinks && this.renderSocials()}
				{assets.bottomImage && <img src={assets.bottomImage} class={s.bottomImage} alt={assets.bottomImageAltText || ''} />}
			</div>
		);
	}

	render() {
		return (
			<div class={s.container}>
				{this.renderFinalShareable()}
			</div>
		);
	}
}